import * as _ from 'underscore'

import { TypeHelper } from '../../../helpers'

import { IMRBaseControlOptions } from './base.control.interfaces'


type WaitForOnInitializedOptions<TInitializedArgs> = {
    onInitialized?: ((e: TInitializedArgs) => void) | Function,
    onError?: Function
}

/**
 * Base Angular controller for components.
 * Should be used to standardize the way components are implemented and how their content is loaded.
 * 
 * @example
 * 
 * Basic usage:
 *
 * export class MyComponentController 
 *  extends
 *      IMRBaseControlController<IComponent, ComponentOptions>
 *  implements
 *      IComponent {
 * 
 *      //#region Lifecycle
 *
 *      protected initialize(options: ComponentOptions): JQueryPromise<void> | void {
 * 
 *          // Inner components initialization goes here
 * 
 *      }
 *      
 *      //#endregion
 * }
 */
export abstract class IMRBaseControlController<TComponent, TOptions extends IMRBaseControlOptions<TComponent, any>>
    implements
    ng.IComponentController {

    //#region 

    protected options: TOptions

    //#endregion


    //#region ng.IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any>; }): void {
        if (this.isOptionsDefined(changes)) {
            const initializeResult = this.initialize(this.options)

            if (TypeHelper.isPromise(initializeResult)) {
                (initializeResult as JQueryPromise<void>).then(() => {
                    this.onInitialized()
                })
            }
            else {
                this.onInitialized()
            }
        }
    }

    public $onDestroy(): void {
        this.destroy()
        this.onDisposed()
    };

    //#endregion


    //#region Events

    /**
     * Fires the given event with thie given arguments.
     * Every regiseterd event handler recives and object with the given args (if present) 
     * and a field named component with an instance of the component. 
     * 
     * @returns The value returned by the event handler
     *
     * @example
     * 
     * // With no arguments
     * super.callEvent(this.options.onSubmit)
     * 
     * // With optional arguments
     * super.callEvent(this.options.onSubmit, {
     *  data: []
     * })
     */
    protected callEvent<TEventAction extends (e: TEventArgs) => TEventResult, TEventArgs extends Object, TEventResult extends any>(action: TEventAction, args?: TEventArgs): TEventResult
    protected callEvent<TEventAction extends (e: TEventArgs) => void, TEventArgs extends Object>(action: TEventAction, args?: TEventArgs): void
    protected callEvent<TEventAction extends (e: TEventArgs) => TEventResult, TEventArgs extends Object, TEventResult extends any>(action: TEventAction, args?: TEventArgs): TEventResult {
        if (_.isFunction(action)) {
            const eventArgs: Object = _.assign({
                component: this
            }, args)

            return action(eventArgs as TEventArgs)
        }
    }

    /**
     * Hooks up into the given options onInitialized function and returns a promise, that resolves when the onInitialized function is called.
     * 
     * @returns A promise that resolves with the first argument passed to onInitialized
     */
    protected waitForOnInitialized<TInitializedArgs>(options: WaitForOnInitializedOptions<TInitializedArgs>): JQueryPromise<TInitializedArgs> {
        return TypeHelper.onInitializedAll(options).then(r => _.first(r))
    }

    /**
     * Works like waitForOnInitialized but with a list of options.
     * 
     * @returns A promise that resovles with a list of first arguments passed to each of the onInitialized functions
     */
    protected waitForOnInitializedAll<TInitializedArgs1, TInitializedArgs2>(options1: WaitForOnInitializedOptions<TInitializedArgs1>, options2: WaitForOnInitializedOptions<TInitializedArgs2>): JQueryPromise<[TInitializedArgs1, TInitializedArgs2]>
    protected waitForOnInitializedAll<TInitializedArgs1, TInitializedArgs2, TInitializedArgs3>(options1: WaitForOnInitializedOptions<TInitializedArgs1>, options2: WaitForOnInitializedOptions<TInitializedArgs2>, optionn3: WaitForOnInitializedOptions<TInitializedArgs3>): JQueryPromise<[TInitializedArgs1, TInitializedArgs2, TInitializedArgs3]>
    protected waitForOnInitializedAll(...options: any[]) {
        return TypeHelper.onInitializedAll(...options)
    }

    private onDisposed(): void {
        if (this.options != null && this.options.onDisposed != null) {
            this.options.onDisposed()
        }
    }

    private onInitialized(): void {
        if (this.options != null && this.options.onInitialized != null) {
            this.options.onInitialized({
                component: this
            })
        }
    }

    //#endregion


    //#region Lifecycle

    /**
     * Called when component's options has been set.
     * Should be used to initialize inner components.
     * The onInitialized event is fired after this functions returns or the promise resolves.
     * 
     * @returns Nothing or a Promise that should resolve after every component has initialized.
     */
    protected abstract initialize(options: TOptions): JQueryPromise<void> | void

    /**
     * Called when Angular removes this component.
     * The onDisposed event is fired after this function has returned.
     */
    protected destroy(): void { }

    //#endregion


    //#region Utils

    private isOptionsDefined(changes: { [index: string]: ng.IChangesObject<any>; }) {
        const optionsChanges: ng.IChangesObject<any> = changes['options']

        return optionsChanges != null && optionsChanges.currentValue != null && optionsChanges.isFirstChange
    }

    //#endregion
}
